import React, { useState, useCallback } from 'react';

import { Modal } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faPaperclip, faSpinner, faXmark, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import Dropzone from 'react-dropzone';
import { nanoid } from 'nanoid';

import { Button } from 'components/ui/button';

interface StagedFile {
  id: string;
  name: string;
  file: File;
  loading: boolean;
  error?: string | null;
}

interface UploadPdfModalProps {
  show: boolean;
  onHide: () => void;
  onUpload?: (files: File[]) => Promise<void>;
}

export default function UploadPdfModal({ show, onHide, onUpload }: UploadPdfModalProps) {
  const MAX_FILE_SIZE = 50 * 1024 * 1024;

  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      id: nanoid(),
      name: file.name,
      file: file,
      loading: true
    }));

    setStagedFiles(prev => [...prev, ...newFiles]);

    newFiles.forEach((newFile) => {
      const validation = validateStagedFile(newFile.file);

      setTimeout(() => {
        setStagedFiles(prev => prev.map(
          f => f.id === newFile.id ? { ...f, loading: false, error: validation.error } : f)
        );
      }, 500);
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setStagedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const handleClose = useCallback(() => {
    setStagedFiles([]);
    setUploading(false);
    onHide();
  }, [onHide]);

  const handleUpload = useCallback(async () => {
    if (onUpload) {
      setUploading(true);
      try {
        await onUpload(stagedFiles.map(f => f.file));
        handleClose();
      } catch (_error: any) {
        setUploading(false);
      }
    } else {
      handleClose();
    }
  }, [stagedFiles, onUpload, handleClose]);

  const hasFiles = stagedFiles.length > 0;
  const isAnyLoading = stagedFiles.some(f => f.loading);
  const anyInvalidFiles = stagedFiles.some(f => f.error);
  const canUpload = hasFiles && !isAnyLoading && !uploading && !anyInvalidFiles;

  const validateStagedFile = (file: File) => {
    // dont have to check if the file is not pdf - dropzone blocks non-pdf files
    // only have to check file size
    if (file.size > MAX_FILE_SIZE) {
      return { error: `File too large` };
    }

    return { error: null } ;
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Upload</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Dropzone accept="application/pdf" onDrop={handleDrop}>
          {({ getRootProps, getInputProps }) => (
            <div
              className="d-flex flex-column align-items-center justify-content-center p-5 border border-2 border-dashed rounded"
              style={{ cursor: 'pointer' }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <div
                className="d-flex align-items-center justify-content-center mb-3 rounded"
                style={{ width: '64px', height: '64px', backgroundColor: '#e8f0fe' }}
              >
                <FontAwesomeIcon className="text-primary" icon={faUpload} size="xl" />
              </div>
              <span>
                Drop files here or <span className="text-primary">click to upload</span>
              </span>
              <span className="text-muted mt-1">We support PDF files up to 50MB</span>
            </div>
          )}
        </Dropzone>

        {hasFiles && (
          <div className="mt-3 overflow-y-auto" style={{ maxHeight: 'min(75vh, 400px)' }}>
            {stagedFiles.map(file => (
              <div
                className="d-flex align-items-center py-2 border-bottom"
                key={file.id}
              >
                <FontAwesomeIcon
                  className={file.loading ? 'text-muted fa-spin' : 'text-muted'}
                  icon={file.loading ? faSpinner : faPaperclip}
                />
                <span className={`ms-2 ${!file.error ? 'flex-grow-1' : ''}`}>{file.name}</span>
                {file.error && (
                  <div className={`text-danger ms-2 ${file.error ? 'flex-grow-1' : ''}`}>
                    <span className="badge rounded-pill text-harvest-gold-600 border-harvest-gold-600">
                      <FontAwesomeIcon icon={faExclamationCircle} />
                      {file.error}
                    </span>
                  </div>
                )}
                {!file.loading && (
                  <button
                    className="btn btn-link p-0 text-muted"
                    type="button"
                    onClick={() => handleRemoveFile(file.id)}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end gap-2 w-100">
          <Button label="Cancel" variant="ghost" onClick={handleClose} />
          <Button disabled={!canUpload} onClick={handleUpload}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
